import React, {useState, useEffect, useRef} from "react";
import {Grid, Step, StepLabel, Stepper, TextField} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import clsx from "clsx";
import Title from "../dashboard/Title";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextInput from "./input/TextInput";
import AutocompleteInput from "./input/AutocompleteInput";
import SelectInput from "./input/SelectInput";
import DateInput from "./input/DateInput";
import DateTimeInput from './input/DateTimeInput'
import UserService from "../../services/user.service";
import {logOut} from "../../helpers/logout";
import {Redirect, useParams} from "react-router-dom";
import {NotificationManager} from 'react-notifications';
import {checkEqualityOfObjects} from '../../helpers/helper'
import Popup from "../presentation/Popup";
import towns from "../../assets/towns.json";
import IdCardScanner from "./IdCardScanner";
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import GraphicEqIcon from '@material-ui/icons/GraphicEq';
import LoadingOverlay from "../presentation/LoadingOverlay";
import {useDashboardStyles, BRAND} from "../settings/dashboardStyles";

const funeralAiFields = [
    'contracting_authority', 'funeral_type', 'funeral_variant', 'ca_address', 'ca_zip', 'ca_city', 'ca_state',
    'ca_nationality', 'ca_email', 'ca_phone', 'ca_id', 'ca_birth', 'ca_relationship',
    'deceased', 'd_type', 'd_maiden_name', 'd_address', 'd_zip', 'd_city', 'd_state', 'd_place_birth',
    'd_gender', 'd_marital_status', 'd_birth', 'd_death', 'd_place_death', 'd_id', 'd_nationality',
    'd_education', 'd_last_job', 'd_wife_husband', 'd_wife_husband_id',
    'planned_pickup_place', 'register_place', 'planned_pickup', 'town_funeral', 'place_funeral', 'date_funeral',
    'coffin_arrival', 'coffin_arrival_place', 'urn_funeral_place', 'urn_funeral_date', 'picked_items', 'notes',
    'deceased_notes'
];

const funeralAiDateFields = ['ca_birth', 'd_birth', 'd_death'];
const funeralAiDateTimeFields = ['planned_pickup', 'date_funeral', 'coffin_arrival', 'urn_funeral_date'];

const getSupportedAudioMimeType = () => {
    if (!window.MediaRecorder || !MediaRecorder.isTypeSupported) return '';
    return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/wav']
        .find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || '';
};

const audioExtensionFromMimeType = (mimeType) => {
    const mime = (mimeType || '').toLowerCase();
    if (mime.includes('webm')) return 'webm';
    if (mime.includes('mp4')) return 'mp4';
    if (mime.includes('aac')) return 'aac';
    if (mime.includes('mpeg') || mime.includes('mp3')) return 'mp3';
    if (mime.includes('wav')) return 'wav';
    return 'webm';
};

const getBirthDateFromBirthNumber = (birthNumber) => {
    const digits = (birthNumber || '').replace(/\D/g, '');
    if (!/^\d{9,10}$/.test(digits)) return null;

    const yearPart = parseInt(digits.slice(0, 2), 10);
    const encodedMonth = parseInt(digits.slice(2, 4), 10);
    const day = parseInt(digits.slice(4, 6), 10);
    let month = encodedMonth;

    if (encodedMonth >= 71 && encodedMonth <= 82) {
        month = encodedMonth - 70;
    } else if (encodedMonth >= 51 && encodedMonth <= 62) {
        month = encodedMonth - 50;
    } else if (encodedMonth >= 21 && encodedMonth <= 32) {
        month = encodedMonth - 20;
    }

    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    const year = digits.length === 9 || yearPart >= 54 ? 1900 + yearPart : 2000 + yearPart;
    const birthDate = new Date(year, month - 1, day);

    if (
        birthDate.getFullYear() !== year ||
        birthDate.getMonth() !== month - 1 ||
        birthDate.getDate() !== day
    ) {
        return null;
    }

    return birthDate;
};

const birthNumberToDateField = {
    ca_id: 'ca_birth',
    d_id: 'd_birth',
};

const initialValues = {
    contracting_authority: '',
    funeral_type: '',
    ca_address: '',
    ca_zip: '',
    ca_city: '',
    ca_state: 'Slovensko',
    ca_nationality: 'slovenská',
    ca_email: '',
    ca_phone: '',
    ca_id: '',
    ca_birth: null,
    ca_relationship: '',
    deceased: '',
    d_type: '',
    d_maiden_name: '',
    d_address: '',
    d_zip: '',
    d_city: '',
    d_state: 'Slovensko',
    d_place_birth: '',
    d_gender: '',
    d_marital_status: '',
    d_birth: null,
    d_death: null,
    d_place_death: '',
    d_id: '',
    d_nationality: 'slovenská',
    d_education: '',
    d_last_job: '',
    d_wife_husband: '',
    d_wife_husband_id: '',
    planned_pickup_place: '',
    register_place: '',
    coffin_arrival_place: '',
    planned_pickup: null,
    town_funeral: '',
    place_funeral: '',
    date_funeral: null,
    coffin_arrival: null,
    urn_funeral_place: '',
    urn_funeral_date: null,
    picked_items: '',
    notes: '',
    deceased_notes: ''
}

const useLocalStyles = makeStyles((theme) => ({
    formContent: {
        width: '100%',
        '& .MuiFormControl-root': {
            marginTop: 0,
            marginBottom: theme.spacing(2),
            marginLeft: 0,
            marginRight: 0,
            width: '100%',
        },
    },
    stepperWrap: {
        background: '#ffffff',
        border: `1px solid ${BRAND.goldSoft}`,
        borderRadius: 12,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(3),
        boxShadow: '0 4px 14px rgba(44, 71, 103, 0.05)',
        '& .MuiStepper-root': {
            background: 'transparent',
            padding: 0,
        },
        '& .MuiStepLabel-label': {
            fontWeight: 600,
            color: BRAND.inkSoft,
        },
        '& .MuiStepLabel-label.MuiStepLabel-active': {
            color: BRAND.deep,
            fontWeight: 700,
        },
        '& .MuiStepLabel-label.MuiStepLabel-completed': {
            color: BRAND.success,
        },
        '& .MuiStepIcon-active': {
            color: BRAND.deep,
        },
        '& .MuiStepIcon-completed': {
            color: BRAND.success,
        },
    },
    actionsBar: {
        marginTop: theme.spacing(3),
        display: 'flex',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        [theme.breakpoints.down('xs')]: {
            '& > button': {
                flex: '1 1 auto',
            },
        },
    },
    actionsLeft: {
        display: 'flex',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    actionsRight: {
        marginLeft: 'auto',
        display: 'flex',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        [theme.breakpoints.down('xs')]: {
            marginLeft: 0,
        },
    },
    scanCta: {
        marginBottom: theme.spacing(2),
    },
    warningText: {
        color: '#a8324a',
        fontWeight: 600,
        fontSize: '1rem',
        marginBottom: theme.spacing(1),
    },
    warningSubtitle: {
        color: BRAND.deep,
        fontWeight: 700,
        marginTop: theme.spacing(2),
        fontSize: '0.95rem',
    },
    warningBody: {
        color: BRAND.inkSoft,
        fontSize: '0.9rem',
        lineHeight: 1.55,
        marginLeft: theme.spacing(2),
        '& strong': {color: BRAND.deep},
    },
    aiNotice: {
        color: BRAND.inkSoft,
        fontSize: '0.9rem',
        lineHeight: 1.55,
        marginBottom: theme.spacing(2),
    },
    aiConflictBox: {
        border: '1px solid #f2b84b',
        borderRadius: 10,
        background: '#fff8e8',
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
    },
    aiFieldWarning: {
        borderColor: '#f2b84b',
        boxShadow: '0 0 0 2px rgba(242, 184, 75, 0.18)',
    },
    aiModalWrap: {
        position: 'relative',
        minHeight: 260,
    },
    aiModalContentLoading: {
        filter: 'blur(2px)',
        pointerEvents: 'none',
        userSelect: 'none',
        opacity: 0.55,
    },
    aiLoadingOverlay: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(2),
        zIndex: 3,
        color: BRAND.deep,
        fontWeight: 700,
        textAlign: 'center',
    },
}));

function getSteps() {
    return ['Obstarávateľ pohrebu', 'Zosnulá osoba', 'Detaily pohrebu'];
}

const propsDelegationCheck = [
    "contracting_authority", "ca_address", "ca_zip", "ca_city", "ca_state", "ca_nationality", "ca_phone",
    "ca_id", "ca_birth", "ca_relationship", "ca_email", "deceased", "d_maiden_name", "d_address", "d_zip", "d_city", "d_state", "d_marital_status", "d_gender",
    "d_last_job", "d_id", "d_place_birth", "d_place_death", "d_education",
    "d_wife_husband", "d_wife_husband_id","d_birth", "d_death"
]
const propsItemsCheck = [
    "picked_items",
    "contracting_authority"
]

const propsValuablesCheck = [
    "contracting_authority"
]

const propsUrnCheck = [
    "deceased", "d_id",
    "contracting_authority"
]
const propsDocumentationCheck = [
    "contracting_authority"
]
const propsAllowCheck = [
    "town_funeral", "deceased", "d_id"
]

export default function FuneralForm(props) {
    const classes = useDashboardStyles();
    const localClasses = useLocalStyles();
    const fixedHeightPaper = clsx(classes.paper, localClasses.formContent);

    const [values, setValues] = useState(initialValues);
    const [oldValues, setOldValues] = useState(initialValues);
    const [success, setSuccess] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const steps = getSteps();
    const [show, setShow] = useState(false);
    const [scannerOpen, setScannerOpen] = useState(false);
    const [scanType, setScanType] = useState('contracting_authority');
    const [user, setUser] = useState(null);
    const [registryOffices, setRegistryOffices] = useState([]);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiTranscript, setAiTranscript] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [aiRecording, setAiRecording] = useState(false);
    const [aiSuggestions, setAiSuggestions] = useState({});
    const placeDeathTouchedRef = useRef(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    const handleChange = (event) => {
        const {name, value} = event.target;
        if (name === 'd_place_death') {
            placeDeathTouchedRef.current = true;
        }

        const nextValues = {...values, [name]: value};
        const birthDateField = birthNumberToDateField[name];
        const birthDate = birthDateField ? getBirthDateFromBirthNumber(value) : null;
        if (birthDate) {
            nextValues[birthDateField] = birthDate;
        }

        setValues(nextValues);
    }

    const autocompleteChange = (event, vals) => {
        setValues({...values, town_funeral: vals ? vals.title : ''});
    }

    const handleOpenScanner = (type) => {
        setScanType(type);
        setScannerOpen(true);
    };

    const handleScanComplete = (scannedData) => {
        setValues({...values, ...scannedData});
        setScannerOpen(false);
        NotificationManager.success('Údaje z občianskeho preukazu boli načítané!', 'Úspech', 5000);
    };

    const normalizeAiValue = (name, value) => {
        if ((funeralAiDateFields.includes(name) || funeralAiDateTimeFields.includes(name)) && typeof value === 'string') {
            return new Date(value);
        }
        return value;
    };

    const getComparableValue = (value) => {
        if (!value) return '';
        if (value instanceof Date) return value.toISOString().slice(0, 10);
        return String(value).trim();
    };

    const currentAiValues = () => {
        const data = {};
        funeralAiFields.forEach((field) => {
            data[field] = getComparableValue(values[field]);
        });
        return data;
    };

    const applyAiResult = (result) => {
        const fields = result && result.fields ? result.fields : {};
        const nextValues = {...values};
        const conflicts = {};

        Object.keys(fields).forEach((name) => {
            if (!funeralAiFields.includes(name)) return;

            const suggestedValue = fields[name];
            const currentValue = values[name];
            const comparableCurrent = getComparableValue(currentValue);
            const comparableSuggested = getComparableValue(suggestedValue);

            if (!comparableSuggested || comparableCurrent === comparableSuggested) return;

            if (!comparableCurrent) {
                nextValues[name] = normalizeAiValue(name, suggestedValue);
            } else {
                conflicts[name] = {
                    currentValue: comparableCurrent,
                    suggestedValue,
                    confidence: result.confidence && result.confidence[name],
                    source: result.sources && result.sources[name],
                };
            }
        });

        setValues(nextValues);
        setAiSuggestions(conflicts);

        const filledCount = Object.keys(fields).length - Object.keys(conflicts).length;
        if (filledCount > 0) {
            NotificationManager.success('AI návrhy boli doplnené do prázdnych polí.', 'AI vyplnenie', 5000);
        }
        if (Object.keys(conflicts).length > 0) {
            NotificationManager.warning('Niektoré AI návrhy sa líšia od existujúcich hodnôt. Skontrolujte ich nižšie.', 'AI návrhy', 7000);
        }
        if (result.warnings && result.warnings.length) {
            NotificationManager.warning(result.warnings.join(' '), 'AI upozornenie', 7000);
        }
    };

    const handleAiMapText = (text) => {
        const cleanText = (text || '').trim();
        if (!cleanText) {
            NotificationManager.warning('Najprv zadajte text alebo potvrďte prepis.', 'AI vyplnenie', 5000);
            return;
        }

        setAiLoading(true);
        UserService.mapFuneralTextWithAi({text: cleanText, currentValues: currentAiValues()}).then(
            (response) => {
                applyAiResult(response.data);
                setAiOpen(false);
                resetAiModalInputs();
                setAiLoading(false);
            },
            (e) => {
                setAiLoading(false);
                if (e.response && e.response.status === 401) {
                    logOut(props);
                    NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                } else {
                    NotificationManager.error('AI spracovanie zlyhalo. Skúste neskôr.', 'Chyba', 5000);
                }
            }
        );
    };

    const startAiRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            const mimeType = getSupportedAudioMimeType();
            const recorder = new MediaRecorder(stream, mimeType ? {mimeType} : undefined);
            audioChunksRef.current = [];
            mediaRecorderRef.current = recorder;

            recorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            recorder.onstop = () => {
                stream.getTracks().forEach((track) => track.stop());
                const recordedMimeType = recorder.mimeType || mimeType || 'audio/webm';
                const blob = new Blob(audioChunksRef.current, {type: recordedMimeType});
                const formData = new FormData();
                formData.append('audio', blob, `funeral-audio.${audioExtensionFromMimeType(recordedMimeType)}`);
                setAiLoading(true);
                UserService.transcribeFuneralAudioWithAi(formData).then(
                    (response) => {
                        setAiTranscript(response.data.transcript || '');
                        setAiLoading(false);
                    },
                    (e) => {
                        setAiLoading(false);
                        if (e.response && e.response.status === 401) {
                            logOut(props);
                            NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                        } else {
                            NotificationManager.error('Prepis nahrávky zlyhal. Skúste neskôr.', 'Chyba', 5000);
                        }
                    }
                );
            };

            recorder.start();
            setAiRecording(true);
        } catch (e) {
            NotificationManager.error('Nepodarilo sa spustiť mikrofón.', 'Chyba', 5000);
        }
    };

    const stopAiRecording = () => {
        if (mediaRecorderRef.current && aiRecording) {
            mediaRecorderRef.current.stop();
            setAiRecording(false);
        }
    };

    const acceptAiSuggestion = (name) => {
        const suggestion = aiSuggestions[name];
        if (!suggestion) return;
        setValues({...values, [name]: normalizeAiValue(name, suggestion.suggestedValue)});
        const nextSuggestions = {...aiSuggestions};
        delete nextSuggestions[name];
        setAiSuggestions(nextSuggestions);
    };

    const rejectAiSuggestion = (name) => {
        const nextSuggestions = {...aiSuggestions};
        delete nextSuggestions[name];
        setAiSuggestions(nextSuggestions);
    };

    const resetAiModalInputs = () => {
        setAiTranscript('');
    };

    const handleCloseAiModal = (open) => {
        setAiOpen(open);
        if (!open) {
            resetAiModalInputs();
        }
    };

    const getAiInputProps = (name) => aiSuggestions[name] ? {InputProps: {classes: {notchedOutline: localClasses.aiFieldWarning}}} : {};

    const params = useParams();
    const hasProperty = params.hasOwnProperty('funeral_id');

    useEffect(() => {
        const user_local = JSON.parse(localStorage.getItem("user"));
        if (user_local && user_local.id) {
            UserService.getUser(user_local.id).then(
                (response) => {
                    setUser(response.data);
                },
                (e) => {
                    if (e.response && e.response.status === 401) {
                        logOut(props);
                        NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                    }
                }
            );
        }

        if (params.hasOwnProperty('funeral_id')) {
            UserService.getFuneral(params.funeral_id).then(
                (response) => {
                    let object = response.data;
                    object.coffin_arrival = object.coffin_arrival ? new Date(object.coffin_arrival) : null;
                    object.ca_birth = object.ca_birth ? new Date(object.ca_birth) : null;
                    object.d_birth = object.d_birth ? new Date(object.d_birth) : null;
                    object.d_death = object.d_death ? new Date(object.d_death) : null;
                    object.planned_pickup = object.planned_pickup ? new Date(object.planned_pickup) : null;
                    object.date_funeral = object.date_funeral ? new Date(object.date_funeral) : null;
                    object.urn_funeral_place = object.urn_funeral_place ? object.urn_funeral_place : '';
                    object.urn_funeral_date = object.urn_funeral_date ? new Date(object.urn_funeral_date) : null;
                    object.d_maiden_name = object.d_maiden_name ? object.d_maiden_name : '';
                    setValues(object);
                    setOldValues(object);
                    // POPUP O TOM ZE PODPISANE DOK BUDU VYMAZANE AK SA PREPISU
                    // if (object.signatures.delegation_signature || object.signatures.items_signature ||
                    //     object.signatures.valuables_signature || object.signatures.urn_signature
                    // || object.signatures.allow_signature || object.signatures.documentation_signature) {
                    //     setShow(true);
                    // }
                },
                (e) => {
                    if (e.response.status === 401) {
                        logOut(props);
                        NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                    }
                }
            );
        }
    }, [params, props]);

    useEffect(() => {
        UserService.getRegistryOffices().then(
            (response) => {
                setRegistryOffices(Array.isArray(response.data) ? response.data : []);
            },
            (e) => {
                if (e && e.response && e.response.status === 401) {
                    logOut(props);
                }
            }
        );
    }, [props]);

    useEffect(() => {
        if (!placeDeathTouchedRef.current) return;
        if (!registryOffices.length) return;

        const needle = (values.d_place_death || '').trim().toLowerCase();
        if (!needle) return;

        const timer = setTimeout(() => {
            const match = registryOffices.find((office) =>
                (office.places || []).some((place) => (place || '').trim().toLowerCase() === needle)
            );
            if (match) {
                setValues((prev) => ({...prev, register_place: match.office}));
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [values.d_place_death, registryOffices]);

    const checkSignatures = (tag, propsToCheck) => {
        if (values.signatures[`${tag}_signature`]) {
            if (!checkEqualityOfObjects(values, oldValues, propsToCheck)) {
                let data = {
                    tag: tag,
                    signatures_id: values.signatures._id
                };
                UserService.unSignDocument(data)
                    .then(() =>{}, (e =>{console.log(e)}))
            }
        }
    }

    const handleCreate = (e) => {
        e.preventDefault();
        // if(hasProperty){
        //     VYMAZANIE PODPISU AK SA UPRAVILI DANE DOKUMENTY
        //     checkSignatures('delegation', propsDelegationCheck);
        //     checkSignatures('items', propsItemsCheck);
        //     checkSignatures('valuables', propsValuablesCheck);
        //     checkSignatures('documentation', propsDocumentationCheck);
        //     checkSignatures('urn', propsUrnCheck);
        //     checkSignatures('allow', propsAllowCheck);
        //
        // }
        if (validate()) {
            if(hasProperty){
                UserService.updateFuneral(values).then(
                    (response) => {
                        setSuccess(response.data._id);
                        NotificationManager.success('Pohreb bol upravený!', 'Vytvorené', 5000);
                    },
                    (e) => {
                        if (e.response.status === 401) {
                            logOut(props);
                            NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                        } else {
                            NotificationManager.error('Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.', 'Chyba', 5000);
                        }
                        handleReset();
                    }
                );
            } else {
                UserService.createFuneral(values).then(
                    (response) => {
                        setSuccess(response.data._id);
                        NotificationManager.success('Pohreb bol vytvorený!', 'Vytvorené', 5000);
                    },
                    (e) => {
                        if (e.response.status === 401) {
                            logOut(props);
                            NotificationManager.warning('Potrebné sa znova prihlásiť!', 'Upozornenie', 5000);
                        } else {
                            NotificationManager.error('Došlo k neočakávanej chybe. Skúste neskôr alebo kontaktujte podporu.', 'Chyba', 5000);
                        }
                        handleReset();
                    }
                );
            }
        } else {
            NotificationManager.error('Skontrolujte vyplnený formulár!', 'Chyba', 5000);
        }

    };

    const [errors, setErrors] = useState({});

    const validate = () => {
        let temp = {};
        temp.contracting_authority = values.contracting_authority ? "" : "Toto pole je potrebné vyplniť!";
        temp.ca_phone = values.ca_phone ? "" : "Toto pole je potrebné vyplniť!";
        temp.deceased = values.deceased ? "" : "Toto pole je potrebné vyplniť!";
        temp.ca_relationship = values.ca_relationship ? "" : "Toto pole je potrebné vyplniť!";
        temp.ca_birth = values.ca_birth ? "" : "Toto pole je potrebné vyplniť!";
        temp.ca_id = (/[0-9]{6}\/?[0-9]{3,4}/).test(values.ca_id) ? "" : "Zadajte požadovaný formát 123456/0000 alebo 1234560000. (prípadne 9 cifier)";
        temp.d_birth = values.d_birth ? "" : "Toto pole je potrebné vyplniť!";
        temp.d_place_birth = values.d_place_birth ? "" : "Toto pole je potrebné vyplniť!";
        temp.d_marital_status = values.d_marital_status ? "" : "Toto pole je potrebné vyplniť!";
        temp.d_gender = values.d_gender ? "" : "Toto pole je potrebné vyplniť!";
        temp.d_nationality= values.d_nationality ? "" : "Toto pole je potrebné vyplniť!";
        temp.d_education = values.d_education ? "" : "Toto pole je potrebné vyplniť!";
        temp.d_last_job = values.d_last_job ? "" : "Toto pole je potrebné vyplniť!";
        temp.d_death = values.d_death ? "" : "Toto pole je potrebné vyplniť!";
        temp.d_place_death = values.d_place_death ? "" : "Toto pole je potrebné vyplniť!";
        temp.town_funeral = values.town_funeral ? "" : "Toto pole je potrebné vyplniť!";
        temp.d_id = (/[0-9]{6}\/?[0-9]{3,4}/).test(values.d_id) ? "" : "Zadajte požadovaný formát 123456/0000 alebo 1234560000. (prípadne 9 cifier)";
        temp.ca_email = (/$^|.+@.+..+/).test(values.ca_email) ? "" : "Email ma zlý formát!"
        setErrors({...temp})

        // eslint-disable-next-line eqeqeq
        return Object.values(temp).every(x => x == "")
    }

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return <Paper className={fixedHeightPaper}>
                    {user?.premium && (
                        <Grid container spacing={2} style={{marginBottom: 16}}>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<CameraAltIcon />}
                                    onClick={() => handleOpenScanner('contracting_authority')}
                                    fullWidth
                                >
                                    Naskenujte občiansky preukaz obstarávateľa
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                    <TextInput
                        label="Meno a priezvisko *"
                        placeholder="(Titul.) Meno Priezvisko (Titul.)"
                        name={'contracting_authority'}
                        value={values.contracting_authority}
                        error={errors.contracting_authority}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Adresa"
                        placeholder="Ulica a číslo"
                        name={'ca_address'}
                        value={values.ca_address}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="PSČ"
                        placeholder="PSČ"
                        name={'ca_zip'}
                        value={values.ca_zip}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Obec"
                        placeholder="Obec"
                        name={'ca_city'}
                        value={values.ca_city}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Štát"
                        placeholder="Štát"
                        name={'ca_state'}
                        value={values.ca_state}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Národnosť"
                        placeholder="Národnosť"
                        name={'ca_nationality'}
                        value={values.ca_nationality}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Email"
                        name={'ca_email'}
                        error={errors.ca_email}
                        value={values.ca_email}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Telefónne číslo *"
                        name={'ca_phone'}
                        error={errors.ca_phone}
                        value={values.ca_phone}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Rodné číslo *"
                        name={'ca_id'}
                        error={errors.ca_id}
                        value={values.ca_id}
                        onChange={handleChange}
                    />
                    <DateInput
                        label="Dátum narodenia *"
                        name={'ca_birth'}
                        value={values.ca_birth}
                        error={errors.ca_birth}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Príbuzenský vzťah *"
                        name={'ca_relationship'}
                        error={errors.ca_relationship}
                        value={values.ca_relationship}
                        onChange={handleChange}
                    />
                </Paper>
            case 1:
                return <Paper className={fixedHeightPaper}>
                    <Title>Zosnulý</Title>
                    {user?.premium && (
                        <Grid container spacing={2} style={{marginBottom: 16}}>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<CameraAltIcon />}
                                    onClick={() => handleOpenScanner('deceased')}
                                    fullWidth
                                >
                                    Naskenujte občiansky preukaz zosnulého
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                    <TextInput
                        label="Meno a priezvisko *"
                        name={'deceased'}
                        placeholder="(Titul.) Meno Priezvisko (Titul.)"
                        value={values.deceased}
                        error={errors.deceased}
                        onChange={handleChange}
                        {...getAiInputProps('deceased')}
                    />
                    <SelectInput
                        label="Typ zosnulého"
                        name={'d_type'}
                        value={values.d_type}
                        options={[
                            {value: 'Potratený ľudský plod', title: 'Potratený ľudský plod'},
                            {value: 'Predčasne odňatý ľudský plod', title: 'Predčasne odňatý ľudský plod'},
                            {value: 'Mŕtvorodené bábätko', title: 'Mŕtvorodené bábätko'},
                            {value: 'Dieťa do 10 rokov', title: 'Dieťa do 10 rokov'},
                            {value: 'Dospelý zosnulý/á', title: 'Dospelý zosnulý/á'},
                            {value: 'Exhumácia', title: 'Exhumácia'},
                        ]}
                        onChange={handleChange}
                    />
                     <TextInput
                        label="Rodné priezvisko"
                        name={'d_maiden_name'}
                        placeholder="Rodné priezvisko"
                        value={values.d_maiden_name}
                        onChange={handleChange}
                        {...getAiInputProps('d_maiden_name')}
                    />
                    <SelectInput
                        label="Pohlavie *"
                        name={'d_gender'}
                        error={errors.d_gender}
                        value={values.d_gender}
                        options={[
                            {value: '1 - muž', title: '1 - muž'},
                            {value: '2 - žena', title: '2 - žena'},
                            {value: '3 - iné', title: '3 - iné'},
                        ]}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Adresa"
                        placeholder="Ulica a číslo"
                        name={'d_address'}
                        value={values.d_address}
                        onChange={handleChange}
                        {...getAiInputProps('d_address')}
                    />
                    <TextInput
                        label="PSČ"
                        placeholder="PSČ"
                        name={'d_zip'}
                        value={values.d_zip}
                        onChange={handleChange}
                        {...getAiInputProps('d_zip')}
                    />
                    <TextInput
                        label="Obec"
                        placeholder="Obec"
                        name={'d_city'}
                        value={values.d_city}
                        onChange={handleChange}
                        {...getAiInputProps('d_city')}
                    />
                    <TextInput
                        label="Štát"
                        placeholder="Štát"
                        name={'d_state'}
                        value={values.d_state}
                        onChange={handleChange}
                        {...getAiInputProps('d_state')}
                    />
                    <TextInput
                        label="Národnosť *"
                        name={'d_nationality'}
                        error={errors.d_nationality}
                        value={values.d_nationality}
                        onChange={handleChange}
                        {...getAiInputProps('d_nationality')}
                    />
                    <TextInput
                        label="Rodné číslo *"
                        name={'d_id'}
                        error={errors.d_id}
                        value={values.d_id}
                        onChange={handleChange}
                        {...getAiInputProps('d_id')}
                    />
                    <DateInput
                        label="Dátum narodenia *"
                        name={'d_birth'}
                        value={values.d_birth}
                        error={errors.d_birth}
                        onChange={handleChange}
                        {...getAiInputProps('d_birth')}
                    />
                    <TextInput
                        label="Miesto narodenia *"
                        name={'d_place_birth'}
                        error={errors.d_place_birth}
                        value={values.d_place_birth}
                        onChange={handleChange}
                        {...getAiInputProps('d_place_birth')}
                    />
                    <DateInput
                        label="Dátum úmrtia *"
                        name={'d_death'}
                        error={errors.d_death}
                        value={values.d_death}
                        onChange={handleChange}
                        {...getAiInputProps('d_death')}
                    />
                    <TextInput
                        label="Miesto úmrtia *"
                        name={'d_place_death'}
                        error={errors.d_place_death}
                        value={values.d_place_death}
                        onChange={handleChange}
                        {...getAiInputProps('d_place_death')}
                    />

                    <SelectInput
                        label="Rodinný stav *"
                        name={'d_marital_status'}
                        error={errors.d_marital_status}
                        value={values.d_marital_status}
                        options={[
                            {value: '1 - slobodný(-á)', title: '1 - slobodný(-á)'},
                            {value: '2 - ženatý, vydatá', title: '2 - ženatý, vydatá'},
                            {value: '3 - rozvedený(-á)', title: '3 - rozvedený(-á)'},
                            {value: '4 - ovdovený(-á)', title: '4 - ovdovený(-á)'},
                            {value: '5 - reg. partnerstvo', title: '5 - reg. partnerstvo'},
                        ]}
                        onChange={handleChange}
                    />
                    <SelectInput
                        label="Vzdelanie *"
                        name={'d_education'}
                        error={errors.d_education}
                        value={values.d_education}
                        options={[
                            {value: 'Bez vzdelania', title: 'Bez vzdelania'},
                            {value: '1 - základné', title: '1 - základné'},
                            {value: '2 - stredné bez maturity', title: '2 - stredné bez maturity'},
                            {value: '3 - stredné s maturitou', title: '3 - stredné s maturitou'},
                            {value: '4 - vysokoškolské', title: '4 - vysokoškolské'},
                        ]}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Posledné zamestnanie *"
                        name={'d_last_job'}
                        error={errors.d_last_job}
                        value={values.d_last_job}
                        onChange={handleChange}
                        {...getAiInputProps('d_last_job')}
                    />
                    <TextInput
                        label="Partner"
                        name={'d_wife_husband'}
                        value={values.d_wife_husband}
                        placeholder={'(Titul.) Meno Priezvisko (Titul.)'}
                        onChange={handleChange}
                        {...getAiInputProps('d_wife_husband')}
                    />
                    <TextInput
                        label="Rodné číslo partnera"
                        name={'d_wife_husband_id'}
                        value={values.d_wife_husband_id}
                        onChange={handleChange}
                        {...getAiInputProps('d_wife_husband_id')}
                    />
                </Paper>;
            case 2:
                return <Paper className={fixedHeightPaper}>
                    <Title>Detaily pohrebu</Title>

                    <DateTimeInput
                        label="Plánované vyzdvihnutie"
                        name={'planned_pickup'}
                        value={values.planned_pickup}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Adresa plánovaného vyzdvihnutia"
                        value={values.planned_pickup_place}
                        name={'planned_pickup_place'}
                        placeholder="Adresa"
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Matrika"
                        value={values.register_place}
                        name={'register_place'}
                        placeholder="Matrika"
                        onChange={handleChange}
                    />
                    <AutocompleteInput
                        label="Mesto/Obec pohrebu *"
                        name={'town_funeral'}
                        value={values.town_funeral}
                        error={errors.town_funeral}
                        options={towns}
                        onChange={autocompleteChange}
                    />
                    <TextInput
                        label="Miesto pohrebu"
                        value={values.place_funeral}
                        name={'place_funeral'}
                        placeholder="Dom smútku, cintorín, kostol ..."
                        onChange={handleChange}
                    />
                    <DateTimeInput
                        label="Dátum a čas pohrebu"
                        name={'date_funeral'}
                        value={values.date_funeral}
                        onChange={handleChange}
                    />
                    <DateTimeInput
                        label="Dátum a čas príjazdu rakvy"
                        name={'coffin_arrival'}
                        value={values.coffin_arrival}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Adresa príjazdu rakvy"
                        value={values.coffin_arrival_place}
                        name={'coffin_arrival_place'}
                        placeholder="Adresa"
                        onChange={handleChange}
                    />
                    <SelectInput
                        label="Typ pohrebu - výber z možností"
                        name={'funeral_type'}
                        value={values.funeral_type}
                        options={[
                            {value: 'Občiansky', title: 'Občiansky'},
                            {value: 'Rímskokatolícka cirkev', title: 'Rímskokatolícka cirkev'},
                            {value: 'Gréckokatolícka cirkev', title: 'Gréckokatolícka cirkev'},
                            {value: 'Pravoslávna cirkev', title: 'Pravoslávna cirkev'},
                            {value: 'Evanjelická cirkev', title: 'Evanjelická cirkev'},
                            {value: 'Náboženská spoločnosť Jehovovi svedkovia', title: 'Náboženská spoločnosť Jehovovi svedkovia'},
                            {value: 'Reformovaná kresťanská cirkev', title: 'Reformovaná kresťanská cirkev'},
                            {value: 'Babtistická bratská jednota', title: 'Babtistická bratská jednota'},
                            {value: 'Apoštolská cirkev', title: 'Apoštolská cirkev'},
                            {value: 'Kresťanské spoločenstvo', title: 'Kresťanské spoločenstvo'},
                            {value: 'BEZ OBRADU', title: 'BEZ OBRADU'}
                        ]}
                        onChange={handleChange}
                    />
                    <SelectInput
                        label="Druh pohrebu"
                        name={'funeral_variant'}
                        value={values.funeral_variant}
                         options={[
                            {value: 'Krem', title: 'Kremácia'},
                            {value: 'Pohreb do zeme', title: 'Pohreb do zeme'},
                            {value: 'Pohreb do hrobky', title: 'Pohreb do hrobky'},
                        ]}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Prevzaté oblečenie a doplnky"
                        name={'picked_items'}
                        value={values.picked_items}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Miesto pohrebu urny"
                        name={'urn_funeral_place'}
                        value={values.urn_funeral_place}
                        onChange={handleChange}
                    />
                    <DateTimeInput
                        label="Dátum a čas pohrebu urny"
                        name={'urn_funeral_date'}
                        value={values.urn_funeral_date}
                        onChange={handleChange}
                    />
                    <TextInput
                        label="Poznámky ku pohrebu"
                        name={'notes'}
                        value={values.notes}
                        onChange={handleChange}
                    />
                </Paper>;
            default:
                return 'Unknown step';
        }
    }

    return (
        <form autoComplete='off' onSubmit={handleCreate}>
            <Container maxWidth="lg" className={classes.pageContainer}>
                <div className={classes.sectionHeader} style={{marginBottom: 16}}>
                    {hasProperty ? <Title>Úprava pohrebu</Title> : <Title>Vytvoriť nový pohreb</Title>}
                </div>
                <div className={localClasses.stepperWrap}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </div>

                <Grid container spacing={2} style={{marginBottom: 16}}>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<GraphicEqIcon/>}
                            onClick={() => setAiOpen(true)}
                            fullWidth
                        >
                            Hlasové vyplnenie formulára
                        </Button>
                    </Grid>
                </Grid>

                {getStepContent(activeStep)}

                {Object.keys(aiSuggestions).length > 0 && (
                    <Paper className={fixedHeightPaper} style={{marginTop: 16}}>
                        <Title>AI návrhy na kontrolu</Title>
                        <Typography className={localClasses.aiNotice}>
                            Tieto hodnoty sa líšia od už vyplnených polí. Vyberte, či ich chcete nahradiť.
                        </Typography>
                        {Object.keys(aiSuggestions).map((name) => (
                            <div key={name} className={localClasses.aiConflictBox}>
                                <Typography><strong>{name}</strong></Typography>
                                <Typography>Aktuálna hodnota: {aiSuggestions[name].currentValue || '-'}</Typography>
                                <Typography>AI návrh: {aiSuggestions[name].suggestedValue}</Typography>
                                {aiSuggestions[name].confidence !== undefined && (
                                    <Typography>Istota: {Math.round(aiSuggestions[name].confidence * 100)}%</Typography>
                                )}
                                {aiSuggestions[name].source && (
                                    <Typography>Zdroj: „{aiSuggestions[name].source}“</Typography>
                                )}
                                <div style={{display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap'}}>
                                    <Button variant="contained" color="primary" onClick={() => acceptAiSuggestion(name)}>
                                        Nahradiť
                                    </Button>
                                    <Button variant="outlined" onClick={() => rejectAiSuggestion(name)}>
                                        Ponechať pôvodné
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </Paper>
                )}

                <div className={localClasses.actionsBar}>
                    <div className={localClasses.actionsLeft}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            className={classes.secondaryAction}
                        >
                            Späť
                        </Button>
                        <Button
                            id={'aa'}
                            type='submit'
                            className={classes.primaryAction}
                            style={activeStep === steps.length - 1 ? {} : {display: 'none'}}
                        >
                            {hasProperty ? "Uložiť zmeny" : "Vytvoriť pohreb"}
                        </Button>
                        <Button
                            id={'bb'}
                            onClick={handleNext}
                            className={classes.primaryAction}
                            style={activeStep === steps.length - 1 ? {display: 'none'} : {}}
                        >
                            Ďalej
                        </Button>
                    </div>
                    {hasProperty && (
                        <div className={localClasses.actionsRight}>
                            <Button
                                className={classes.secondaryAction}
                                onClick={() => setSuccess(params.funeral_id)}
                            >
                                Späť na detaily
                            </Button>
                        </div>
                    )}
                </div>
            </Container>

            <Popup
                title={'Hlasové vyplnenie formulára'}
                openPopup={aiOpen}
                setOpenPopup={handleCloseAiModal}
                maxW="md"
            >
                <div className={localClasses.aiModalWrap}>
                    {aiLoading && <LoadingOverlay label="Spracúvam formulár..."/>}
                    <div className={aiLoading ? localClasses.aiModalContentLoading : undefined}>
                <Typography className={localClasses.aiNotice}>
                    Hlasové spracovanie odošle zadaný text alebo nahrávku na prepis a vyplnenie polí formulára. Skontrolujte návrhy pred uložením.
                </Typography>
                <div>
                    <div style={{display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap'}}>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={aiRecording || aiLoading}
                            onClick={startAiRecording}
                        >
                            Spustiť nahrávanie
                        </Button>
                        <Button
                            variant="outlined"
                            disabled={!aiRecording}
                            onClick={stopAiRecording}
                        >
                            Zastaviť nahrávanie
                        </Button>
                    </div>
                    {aiRecording && <Typography className={localClasses.warningText}>Nahrávam...</Typography>}
                    {aiLoading && <Typography className={localClasses.aiNotice}>Spracúvam nahrávku...</Typography>}
                    {!aiTranscript.trim() && (
                        <div style={{marginBottom: 16}}>
                            <Typography className={localClasses.aiNotice}>
                                Môžete diktovať prirodzenou rečou alebo text napísať ručne. Napríklad: „Meno zosnulého Ján Novák, dátum narodenia dvanásty marec tisícdeväťstopäťdesiat, pohreb desiateho apríla o štrnástej v Dome smútku v Nitre.“
                            </Typography>
                            <Typography className={localClasses.warningSubtitle}>Obstarávateľ pohrebu</Typography>
                            <Typography component="div" className={localClasses.warningBody}>
                                meno a priezvisko, adresa, PSČ, obec, štát, národnosť, email, telefón, rodné číslo, dátum narodenia, príbuzenský vzťah
                            </Typography>
                            <Typography className={localClasses.warningSubtitle}>Zosnulá osoba</Typography>
                            <Typography component="div" className={localClasses.warningBody}>
                                meno a priezvisko, typ zosnulého, rodné priezvisko, pohlavie, adresa, PSČ, obec, štát, národnosť, rodné číslo, dátum narodenia, miesto narodenia, dátum úmrtia, miesto úmrtia, rodinný stav, vzdelanie, posledné zamestnanie, partner, rodné číslo partnera
                            </Typography>
                            <Typography className={localClasses.warningSubtitle}>Detaily pohrebu</Typography>
                            <Typography component="div" className={localClasses.warningBody}>
                                plánované vyzdvihnutie, adresa vyzdvihnutia, matrika, mesto alebo obec pohrebu, miesto pohrebu, dátum a čas pohrebu, dátum a čas príjazdu rakvy, adresa príjazdu rakvy, typ pohrebu, druh pohrebu, prevzaté oblečenie a doplnky, miesto pohrebu urny, dátum a čas pohrebu urny, poznámky
                            </Typography>
                        </div>
                    )}
                    <TextField
                        variant="outlined"
                        label="Text alebo prepis nahrávky"
                        value={aiTranscript}
                        onChange={(event) => setAiTranscript(event.target.value)}
                        multiline
                        rows={6}
                        fullWidth
                        placeholder="Sem môžete napísať text ručne alebo nahrať hlas a prepis sa zobrazí tu. Pred vyplnením ho môžete upraviť."
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={aiLoading || !aiTranscript.trim()}
                        onClick={() => handleAiMapText(aiTranscript)}
                        style={{marginTop: 16}}
                    >
                        Použiť text na vyplnenie
                    </Button>
                </div>
                    </div>
                </div>

            </Popup>

            <Popup
                title={'Upozornenie pri úprave'}
                openPopup={show}
                setOpenPopup={setShow}
                maxW="sm"
            >
                <Typography className={localClasses.warningText}>
                    Pri úprave dát, ktoré sú uvedené pri podpísaných dokumentoch, sa podpis pri dokumente vymaže!
                </Typography>
                <Typography className={localClasses.warningSubtitle}>
                    Dáta pre Splnomocnenie
                </Typography>
                <Typography component="div" className={localClasses.warningBody}>
                    <strong>Obstarávateľ pohrebu:</strong> meno, adresa, email, tel. číslo, rodné číslo, príbuzenský vzťah
                </Typography>
                <Typography component="div" className={localClasses.warningBody}>
                    <strong>Zosnulý:</strong> meno, rodné číslo, dátum a miesto narodenia a úmrtia, posledné zamestnanie, dosiahnuté vzdelanie, rodinný stav, meno a rodné číslo partnera
                </Typography>
                <Typography className={localClasses.warningSubtitle}>
                    Dáta pre Dokumenty na podpis
                </Typography>
                <Typography component="div" className={localClasses.warningBody}>
                    <strong>Obstarávateľ pohrebu:</strong> meno
                </Typography>
                <Typography component="div" className={localClasses.warningBody}>
                    <strong>Zosnulý:</strong> meno, rodné číslo, mesto pohrebu, prevzaté veci
                </Typography>
            </Popup>
            <IdCardScanner
                open={scannerOpen}
                onClose={() => setScannerOpen(false)}
                onScanComplete={handleScanComplete}
                scanType={scanType}
            />
            {success && <Redirect to={{
                pathname: `/pohreb/${success}/info`,
                state: {from: props.location}
            }}/>}
        </form>
    )
}
